import React from 'react';
import { Button } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { ReportProblemOutlined } from '@mui/icons-material';
import Link from 'next/link';
import Highlight from '../Highlight';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '60px 0',
    maxWidth: 665,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '48px 32px 52px',
    },
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 600,
    margin: '0 0 20px',
  },
  sectionText: {
    fontSize: 16,
  },
  comparisonTableWrapper: {
    marginTop: 40,
    [theme.breakpoints.down('sm')]: {
      // maxWidth: '100%',
      overflowX: 'auto',
      overflowY: 'hidden',
      margin: '40px -32px 0',
      paddingLeft: 16,
      paddingBottom: 16,
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  },
  comparisonTableHead: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '1px',
    backgroundColor: theme.specialColors.CIRCULAR_PROGRESS,
    marginLeft: 133,
    overflow: 'hidden',
    borderRadius: '8px 8px 0 0',
    border: `1px solid ${theme.specialColors.CIRCULAR_PROGRESS}`,
    borderBottom: 'none',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(3, 175px)',
      overflow: 'initial',
    },
  },
  comparisonTable: {
    display: 'grid',
    gridTemplateColumns: '132px 1fr 1fr 1fr',
    gap: '1px',
    backgroundColor: theme.specialColors.CIRCULAR_PROGRESS,
    borderRadius: '8px 0 8px 8px',
    overflow: 'hidden',
    border: `1px solid ${theme.specialColors.CIRCULAR_PROGRESS}`,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '132px repeat(3, 175px)',
      overflow: 'initial',
    },
  },
  columnLabel: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '13px',
    padding: '16px 12px',
    '&:first-child': {
      backgroundColor: '#61C774',
      borderRadius: '8px 0 0 0',
    },
    '&:nth-child(2)': {
      backgroundColor: '#FFCF50',
    },
    '&:nth-child(3)': {
      backgroundColor: '#6CB9FF',
    },
    '&:last-child': {
      borderRadius: '0 8px 0 0',
    },
  },
  rowLabel: {
    fontWeight: 700,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 12px',
  },
  tableDiv: {
    padding: '16px 12px',
    lineHeight: '19px',
    '&:first-child': {
      borderRadius: '8px 0 0 0',
    },
    '&:nth-child(13)': {
      borderRadius: '0 0 0 8px',
    },
    '&:last-child': {
      borderRadius: '0 0 8px 0',
    },
  },
  greenDiv: {
    backgroundColor: '#EDFFF0',
  },
  yellowDiv: {
    backgroundColor: '#FFF2CF',
  },
  blueDiv: {
    backgroundColor: '#DDEFFF',
  },
  btnWrapper: {
    textAlign: 'center',
    marginTop: 60,
    [theme.breakpoints.down('sm')]: {
      marginTop: 36,
    },
  },
  cardBtn: {
    fontSize: 17,
    fontWeight: 600,
    lineHeight: '23.15px',
    borderRadius: '100px',
    minWidth: '206px',
    padding: '10px 26px',
  },
  ctaText: {
    fontSize: 16,
    maxWidth: 430,
    margin: '20px auto 0',
  },
}));

function Comparison() {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <h2 className={classes.sectionTitle}>Paraphrasing vs. Quoting vs. Summarizing</h2>
      <div className={classes.sectionText}>
        <p>
          Paraphrasing, quoting, and summarizing are all ways to incorporate the works of others
          into your writing to build credibility and support for your unique arguments and ideas.
        </p>
        <p>
          These are all important skills to learn and employ when writing and researching, but on
          the surface, it can sometimes be difficult to understand which is most appropriate to use.
          When in doubt, consult our handy table below.
        </p>
      </div>
      <div className={classes.comparisonTableWrapper}>
        <div className={classes.comparisonTableHead}>
          <div className={classes.columnLabel}>Paraphrasing</div>
          <div className={classes.columnLabel}>Quoting</div>
          <div className={classes.columnLabel}>Summarizing</div>
        </div>
        <div className={classes.comparisonTable}>
          {/* <div>&nbsp;</div> */}
          <div className={`${classes.tableDiv} ${classes.rowLabel}`}>Definition</div>
          <div className={`${classes.tableDiv} ${classes.greenDiv}`}>
            Changes the words while maintaining their meaning
          </div>
          <div className={`${classes.tableDiv} ${classes.yellowDiv}`}>
            Directly copies the original words/text
          </div>
          <div className={`${classes.tableDiv} ${classes.blueDiv}`}>
            Condenses larger works down to the main points
          </div>
          <div className={`${classes.tableDiv} ${classes.rowLabel}`}>Output</div>
          <div className={`${classes.tableDiv} ${classes.greenDiv}`}>
            Can be longer or shorter than the original text
          </div>
          <div className={`${classes.tableDiv} ${classes.yellowDiv}`}>
            Is the exact same length as the original text
          </div>
          <div className={`${classes.tableDiv} ${classes.blueDiv}`}>
            Is shorter than the original text
          </div>
          <div className={`${classes.tableDiv} ${classes.rowLabel}`}>Goal</div>
          <div className={`${classes.tableDiv} ${classes.greenDiv}`}>
            Is used to clarify or better explain the original text
          </div>
          <div className={`${classes.tableDiv} ${classes.yellowDiv}`}>
            Is used as evidence or to back up a claim
          </div>
          <div className={`${classes.tableDiv} ${classes.blueDiv}`}>
            Is used to give a broad overview of a source
          </div>
          <div className={`${classes.tableDiv} ${classes.rowLabel}`}>Quotes</div>
          <div className={`${classes.tableDiv} ${classes.greenDiv}`}>
            Must include a citation but does not need quotation marks unless part of the paraphrase
            is a quote
          </div>
          <div className={`${classes.tableDiv} ${classes.yellowDiv}`}>
            Must use quotations marks and include the citation for the original source
          </div>
          <div className={`${classes.tableDiv} ${classes.blueDiv}`}>
            Must include a citation but does not need quotation marks unless part of the summary is
            a quote
          </div>
        </div>
      </div>
      <div className={classes.btnWrapper}>
        <Link href="/">
          <Button variant="contained" color="primary" className={classes.cardBtn}>
            Try the Paraphraser
          </Button>
        </Link>
        <div className={classes.ctaText}>
          QuillBot is trusted by students, writers, and professionals who simply want to write
          better.
        </div>
      </div>
    </div>
  );
}

export default Comparison;
